/**
 * Таблица истории расчётов: колонки, пресеты вида и быстрые фильтры.
 *
 * Чистая логика без React — её проверяет `historyTableLogic.test.ts`. Отрисовка
 * живёт в `HistoryTable.tsx`.
 *
 * Вид (какие колонки видны, в каком порядке, какой ширины) и пользовательские
 * пресеты хранятся в браузере зрителя (`localStorage`): это настройка «как мне
 * удобно смотреть», а не данные расчётов. Хранилища может не быть (приватное
 * окно, запрет хоста) — тогда вид просто не запоминается.
 */

import type { HistoryGroup, HistoryMetrics, RecordSource } from '../../shared/api/client';

export type MetricKey = keyof HistoryMetrics;
export type ColumnKey = 'date' | 'source' | MetricKey;
type ColumnKind = 'money' | 'percent' | 'count' | 'date' | 'text';

export interface ColumnDef {
  key: ColumnKey;
  /** Ключ подписи в `idea_calc.history.table.columns`. */
  labelKey: string;
  kind: ColumnKind;
}

/** Все колонки в порядке по умолчанию. ASIN и название расчёта закреплены и сюда не входят. */
export const COLUMNS: readonly ColumnDef[] = [
  { key: 'date', labelKey: 'date', kind: 'date' },
  { key: 'price', labelKey: 'price', kind: 'money' },
  { key: 'monthly_sales', labelKey: 'monthly_sales', kind: 'count' },
  { key: 'profit_month', labelKey: 'profit_month', kind: 'money' },
  { key: 'cogs', labelKey: 'cogs', kind: 'money' },
  { key: 'profit_unit', labelKey: 'profit_unit', kind: 'money' },
  { key: 'margin', labelKey: 'margin', kind: 'percent' },
  { key: 'roi', labelKey: 'roi', kind: 'percent' },
  { key: 'max_purchase_price', labelKey: 'max_purchase_price', kind: 'money' },
  { key: 'conversion', labelKey: 'conversion', kind: 'percent' },
  { key: 'returns', labelKey: 'returns', kind: 'percent' },
  { key: 'ppc_share', labelKey: 'ppc_share', kind: 'percent' },
  { key: 'order_price', labelKey: 'order_price', kind: 'money' },
  { key: 'ad_per_unit', labelKey: 'ad_per_unit', kind: 'money' },
  { key: 'batch', labelKey: 'batch', kind: 'count' },
  { key: 'batch_cost', labelKey: 'batch_cost', kind: 'money' },
  { key: 'batch_profit', labelKey: 'batch_profit', kind: 'money' },
  { key: 'batch_margin', labelKey: 'batch_margin', kind: 'percent' },
  { key: 'batch_roi', labelKey: 'batch_roi', kind: 'percent' },
  { key: 'source', labelKey: 'source', kind: 'text' },
];

const COLUMN_BY_KEY = new Map(COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS: ColumnKey[] = COLUMNS.map((c) => c.key);

export function columnDef(key: ColumnKey): ColumnDef {
  const def = COLUMN_BY_KEY.get(key);
  if (!def) throw new Error(`history table: неизвестная колонка ${key}`);
  return def;
}

/** Ширина колонки в пикселях: узкая — по умолчанию, широкая — по выбору в «Колонках». */
export function columnWidth(key: ColumnKey, wide: boolean): number {
  const kind = columnDef(key).kind;
  if (kind === 'date') return wide ? 170 : 120;
  if (kind === 'text') return wide ? 140 : 100;
  return wide ? 150 : 110;
}

export interface ViewPreset {
  id: string;
  /** Встроенный пресет — ключ перевода; пользовательский — имя как есть. */
  labelKey?: string;
  name?: string;
  visible: ColumnKey[];
  order: ColumnKey[];
  wide: ColumnKey[];
}

export const BUILTIN_PRESETS: readonly ViewPreset[] = [
  { id: 'main', labelKey: 'main', visible: ['date', 'price', 'monthly_sales', 'profit_month', 'profit_unit', 'margin', 'roi'], order: ALL_KEYS, wide: [] },
  { id: 'batch', labelKey: 'batch', visible: ['date', 'price', 'batch', 'batch_cost', 'batch_profit', 'batch_margin', 'batch_roi'], order: ALL_KEYS, wide: [] },
  { id: 'ads', labelKey: 'ads', visible: ['date', 'conversion', 'returns', 'ppc_share', 'order_price', 'ad_per_unit', 'profit_unit'], order: ALL_KEYS, wide: [] },
  { id: 'all', labelKey: 'all', visible: ALL_KEYS, order: ALL_KEYS, wide: [] },
];

export interface ViewState {
  presetId: string;
  visible: ColumnKey[];
  order: ColumnKey[];
  wide: ColumnKey[];
  custom: ViewPreset[];
}

export const DEFAULT_VIEW: ViewState = {
  presetId: 'main',
  visible: BUILTIN_PRESETS[0].visible,
  order: ALL_KEYS,
  wide: [],
  custom: [],
};

/** Id вида, который изменили руками и ещё не сохранили в пресет. */
export const EDITED_PRESET_ID = 'edited';

export function applyPreset(view: ViewState, presetId: string): ViewState {
  const preset = BUILTIN_PRESETS.find((p) => p.id === presetId) ?? view.custom.find((p) => p.id === presetId);
  if (!preset) return view;
  return { ...view, presetId, visible: [...preset.visible], order: [...preset.order], wide: [...preset.wide] };
}

export function toggleColumn(view: ViewState, key: ColumnKey): ViewState {
  const visible = view.visible.includes(key) ? view.visible.filter((k) => k !== key) : [...view.visible, key];
  return { ...view, visible, presetId: EDITED_PRESET_ID };
}

export function toggleWide(view: ViewState, key: ColumnKey): ViewState {
  const wide = view.wide.includes(key) ? view.wide.filter((k) => k !== key) : [...view.wide, key];
  return { ...view, wide, presetId: EDITED_PRESET_ID };
}

/** Сдвиг колонки на `delta` позиций; за край — без изменений. */
export function moveColumn(view: ViewState, key: ColumnKey, delta: number): ViewState {
  const from = view.order.indexOf(key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= view.order.length) return view;
  const order = [...view.order];
  order.splice(from, 1);
  order.splice(to, 0, key);
  return { ...view, order, presetId: EDITED_PRESET_ID };
}

export function saveAsPreset(view: ViewState, name: string): ViewState {
  const trimmed = name.trim();
  if (!trimmed) return view;
  const id = `custom-${Date.now()}`;
  const preset: ViewPreset = { id, name: trimmed, visible: [...view.visible], order: [...view.order], wide: [...view.wide] };
  return { ...view, presetId: id, custom: [...view.custom, preset] };
}

export function deletePreset(view: ViewState, presetId: string): ViewState {
  const custom = view.custom.filter((p) => p.id !== presetId);
  return { ...view, custom, presetId: view.presetId === presetId ? EDITED_PRESET_ID : view.presetId };
}

/** Видимые колонки в выбранном порядке. */
export function visibleColumns(view: ViewState): ColumnKey[] {
  return view.order.filter((k) => view.visible.includes(k));
}

const STORAGE_KEY = 'idea_calc.history_table.v1';

/** Разобранный вид из хранилища; всё незнакомое (устаревшие колонки, мусор) отбрасывается. */
export function parseStoredView(raw: string | null): ViewState {
  if (!raw) return DEFAULT_VIEW;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_VIEW;
  }
  if (!parsed || typeof parsed !== 'object') return DEFAULT_VIEW;
  const obj = parsed as Record<string, unknown>;
  const keys = (value: unknown): ColumnKey[] =>
    Array.isArray(value) ? value.filter((k): k is ColumnKey => typeof k === 'string' && COLUMN_BY_KEY.has(k as ColumnKey)) : [];
  // Порядок обязан содержать все колонки: новая колонка из следующего релиза
  // встаёт в конец, а не пропадает из «Колонок» навсегда.
  const order = keys(obj.order);
  const fullOrder = [...order, ...ALL_KEYS.filter((k) => !order.includes(k))];
  const custom = Array.isArray(obj.custom)
    ? obj.custom
        .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object' && typeof (p as Record<string, unknown>).id === 'string' && typeof (p as Record<string, unknown>).name === 'string')
        .map((p) => {
          const pOrder = keys(p.order);
          return { id: p.id as string, name: p.name as string, visible: keys(p.visible), order: [...pOrder, ...ALL_KEYS.filter((k) => !pOrder.includes(k))], wide: keys(p.wide) };
        })
    : [];
  return {
    presetId: typeof obj.presetId === 'string' ? obj.presetId : DEFAULT_VIEW.presetId,
    visible: Array.isArray(obj.visible) ? keys(obj.visible) : DEFAULT_VIEW.visible,
    order: fullOrder,
    wide: keys(obj.wide),
    custom,
  };
}

export function loadView(): ViewState {
  try {
    return parseStoredView(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_VIEW;
  }
}

export function storeView(view: ViewState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(view));
  } catch {
    // Хранилище недоступно — вид просто не запомнится до следующего открытия.
  }
}

export interface HistoryRow {
  key: string;
  /** id записи — адрес расчёта `?result=<id>`; у ответа старого формата его нет. */
  id: number | undefined;
  asin: string;
  fileName: string;
  timestamp: string;
  source: RecordSource | undefined;
  metrics: HistoryMetrics | null;
}

/** Плоские строки таблицы: каждая сохранённая версия — отдельная строка, новые сверху. */
export function flattenRows(groups: HistoryGroup[]): HistoryRow[] {
  const rows: HistoryRow[] = [];
  for (const group of groups) {
    for (const v of group.versions) {
      rows.push({
        key: `${group.asin}/${v.fileName}`,
        id: v.id,
        asin: group.asin,
        fileName: v.fileName,
        timestamp: v.timestamp,
        source: v.source,
        metrics: v.metrics ?? null,
      });
    }
  }
  return rows.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export type QuickFilterId = 'margin30' | 'roi100' | 'profitPositive' | 'sales300';

/** Порог — «с учётом всех затрат», как в первом ряду «Результата». Строка без метрик не проходит ни один. */
export const QUICK_FILTERS: readonly { id: QuickFilterId; test: (m: HistoryMetrics) => boolean }[] = [
  { id: 'margin30', test: (m) => m.margin !== null && m.margin >= 30 },
  { id: 'roi100', test: (m) => m.roi !== null && m.roi >= 100 },
  { id: 'profitPositive', test: (m) => m.profit_unit !== null && m.profit_unit > 0 },
  { id: 'sales300', test: (m) => m.monthly_sales !== null && m.monthly_sales >= 300 },
];

export function applyQuickFilters(rows: HistoryRow[], active: QuickFilterId[]): HistoryRow[] {
  if (active.length === 0) return rows;
  const tests = QUICK_FILTERS.filter((f) => active.includes(f.id)).map((f) => f.test);
  return rows.filter((r) => r.metrics !== null && tests.every((test) => test(r.metrics as HistoryMetrics)));
}

/** Значение ячейки для сортировки: пустые — всегда внизу. */
export function sortValue(row: HistoryRow, key: ColumnKey): number | string | null {
  if (key === 'date') return row.timestamp;
  if (key === 'source') return row.source ?? 'ui';
  return row.metrics ? row.metrics[key] : null;
}

export function formatCell(row: HistoryRow, key: ColumnKey, locale: string): string {
  const def = columnDef(key);
  if (def.kind === 'date') {
    if (!row.timestamp) return '';
    try {
      return new Date(row.timestamp).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
      return row.timestamp;
    }
  }
  if (key === 'source') return row.source === 'mcp' ? 'MCP' : 'UI';
  const value = row.metrics ? row.metrics[key as MetricKey] : null;
  if (value === null || value === undefined) return '—';
  if (def.kind === 'money') {
    const abs = Math.abs(value);
    const text = abs >= 1000 ? Math.round(abs).toLocaleString(locale) : abs.toFixed(2);
    return (value < 0 ? '−$' : '$') + text;
  }
  if (def.kind === 'percent') return value.toFixed(1) + '%';
  return Math.round(value).toLocaleString(locale);
}

/**
 * Цвет ячейки: убыток (прибыль, маржа, ROI ниже нуля) — красным, остальное без
 * цвета. Зелёный на каждом плюсе делал таблицу пёстрой и выделял маржу 0.1%
 * наравне с 40% — внимание должно цепляться за минус, а не за всё подряд.
 */
export function cellTone(row: HistoryRow, key: ColumnKey): 'bad' | 'plain' {
  if (!['profit_unit', 'profit_month', 'batch_profit', 'margin', 'roi', 'batch_margin', 'batch_roi'].includes(key)) return 'plain';
  const value = row.metrics ? row.metrics[key as MetricKey] : null;
  if (value === null || value === undefined) return 'plain';
  return value < 0 ? 'bad' : 'plain';
}

/** Ключ группы истории для расчёта с биржи (без ASIN): `EX-<номер карточки>` — так его
 * собирает бэкенд (`backend/services/user_results.py::history_key`). */
const EXCHANGE_KEY_RE = /^EX-(\d{1,12})$/;

/** Номер карточки биржи из ключа группы или `null`, если ключ — ASIN. */
export function exchangeXidFromKey(key: string): string | null {
  return EXCHANGE_KEY_RE.exec(key)?.[1] ?? null;
}

/**
 * Фото групп «Биржа №…» — из самой свежей версии, где оно сохранено. Keepa карточку биржи
 * не знает, поэтому такие группы в запрос фото по ASIN не идут, и другого источника нет.
 */
export function exchangeGroupImages(groups: HistoryGroup[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const group of groups) {
    if (!exchangeXidFromKey(group.asin)) continue;
    const image = group.versions.find((v) => v.image_url)?.image_url;
    if (image) out[group.asin] = image;
  }
  return out;
}

/** Значение ячейки для выгрузки: числа как есть (без $, % и разделителей разрядов), дата — ГГГГ-ММ-ДД. */
export function exportValue(row: HistoryRow, key: ColumnKey): string {
  if (key === 'date') return row.timestamp ? row.timestamp.slice(0, 10) : '';
  if (key === 'source') return row.source === 'mcp' ? 'MCP' : 'UI';
  const value = row.metrics ? row.metrics[key as MetricKey] : null;
  if (value === null || value === undefined) return '';
  return columnDef(key).kind === 'count' ? String(Math.round(value)) : value.toFixed(2);
}

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * CSV таблицы истории: строки — как отфильтрованы, колонки — ASIN, расчёт и видимые
 * в выбранном порядке. Подписи колонок передаёт вызывающий (они переводятся).
 */
export function historyCsv(rows: HistoryRow[], keys: ColumnKey[], header: string[]): string {
  const lines = [header.map(csvField).join(',')];
  for (const row of rows) {
    lines.push([row.asin, row.fileName, ...keys.map((k) => exportValue(row, k))].map(csvField).join(','));
  }
  return lines.join('\r\n');
}
